import { ajaxRetry } from '../ajaxRetry';
import { CredentialsEntity, ResetCredentialsResponse } from '../../models/credentials';
import { RefreshableItem } from '../../models/refreshableItem';

import { ProductNames } from '../../models/product';
import { AuthenticationOption } from '../../connectivity/services/credentialsClient';
import { base, createApiURL, ResponseObj } from './base';

export const getCredentials = (
  id: string,
  retry: boolean
): Promise<ResponseObj<CredentialsEntity>> => {
  return ajaxRetry<CredentialsEntity>(
    {
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/credentials/${id}?detailedError=true&templatesScreen=true`),
    },
    () => retry
  ).then((response: ResponseObj<CredentialsEntity>) => {
    const credentialsResponse: ResponseObj<CredentialsEntity> = {
      ...response,
      data: response.data,
    };

    return credentialsResponse;
  });
};

export const getQR = (id: string): Promise<ResponseObj<string>> => {
  return ajaxRetry({
    headers: { ...base.headers(), Accept: 'text/html' },
    method: 'GET',
    url: createApiURL(`/api/v1/credentials/${id}/qr`),
  });
};

export const supplement = (
  id: string,
  information: Record<string, unknown> | null
): Promise<ResponseObj<void>> => {
  return ajaxRetry({
    data: { information },
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/credentials/${id}/supplemental-information`),
  });
};

export type CredentialsRequest = {
  providerName: string;
  fields: Record<string, string>;
  callbackUri?: string;
  appUri?: string;
  triggerRefresh?: boolean;
  productNames: ProductNames[];
  selectedAuthenticationOptions?: AuthenticationOption[];
};

type CreateCredentialsRequest = {
  request: CredentialsRequest;
  refreshableItems: RefreshableItem[];
};

export const createCredentials = ({
  request,
  refreshableItems,
}: CreateCredentialsRequest): Promise<ResponseObj<CredentialsEntity>> => {
  const query = refreshableItems.map((item) => `items=${item}`).join('&');

  return ajaxRetry({
    data: JSON.stringify(request),
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/credentials?${query}`),
  });
};

type UpdateCredentialsRequest = {
  request: CredentialsRequest;
  id: string;
};

export const updateCredentials = ({
  id,
  request,
}: UpdateCredentialsRequest): Promise<ResponseObj<CredentialsEntity>> => {
  return ajaxRetry({
    data: JSON.stringify(request),
    headers: base.headers(),
    method: 'PUT',
    url: createApiURL(`/api/v1/credentials/${id}`),
  });
};

export type RefreshCredentialsRequest = {
  id: string;
  authenticate: boolean;
  optIn?: boolean;
  request: Pick<CredentialsRequest, 'appUri' | 'callbackUri' | 'productNames'>;
  refreshableItems?: RefreshableItem[];
};

export const refreshCredentials = ({
  id,
  authenticate,
  request,
  refreshableItems = [],
  optIn,
}: RefreshCredentialsRequest): Promise<ResponseObj<null>> => {
  const query = refreshableItems.map((item) => `items=${item}`).join('&');

  const params = new URLSearchParams(query);
  params.append('authenticate', authenticate.toString());

  if (optIn !== undefined) {
    params.append('optIn', optIn.toString());
  }

  return ajaxRetry<null>({
    data: JSON.stringify(request),
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/credentials/${id}/refresh?${params}`),
  });
};

export type AuthenticateCredentialsRequest = {
  id: string;
  request: Pick<CredentialsRequest, 'appUri' | 'callbackUri' | 'productNames'>;
};

export const authenticateCredentials = ({
  id,
  request,
}: AuthenticateCredentialsRequest): Promise<ResponseObj<null>> => {
  return ajaxRetry<null>({
    data: JSON.stringify(request),
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/credentials/${id}/authenticate`),
  });
};

export type ResetCredentialsStatusRequest = {
  credentialsId: string;
};

export const resetCredentialsStatus = ({ credentialsId }: ResetCredentialsStatusRequest) => {
  return ajaxRetry<ResetCredentialsResponse>({
    headers: base.headers(),
    method: 'POST',
    url: createApiURL(`/api/v1/credentials/operations/abort?credentials_id=${credentialsId}`),
  });
};

export const getCredentialsResetStatus = ({ credentialsId }: ResetCredentialsStatusRequest) => {
  return ajaxRetry<ResetCredentialsResponse>({
    headers: base.headers(),
    method: 'GET',
    url: createApiURL(`/api/v1/credentials/operations/abort?credentials_id=${credentialsId}`),
  });
};

export const supplementalInfoApiCallback = (
  supplementalInfoState: string
): Promise<ResponseObj<string>> => {
  return ajaxRetry({
    headers: base.headers(),
    method: 'GET',
    url: createApiURL(`/api/v1/credentials/third-party/callback?state=${supplementalInfoState}`),
  });
};

export const deleteCredentials = (credentialsId: string): Promise<ResponseObj<null>> => {
  return ajaxRetry({
    headers: base.headers(),
    method: 'DELETE',
    url: createApiURL(`/api/v1/credentials/${credentialsId}`),
  });
};
